/**
 * Client-side entry point
 * Hydrates the server-rendered HTML with Vue
 */

import { createApp } from './main.js';
import { initAuth } from './stores/auth';
import { CONFIG } from './config';
import { getLocaleFromCookie, setLocaleCookie } from './utils/localeCookie.js';
import { mergeLocaleMessagesDeep } from './utils/mergeLocaleMessages.js';
import { escapeVueI18nAtInTranslationsTree } from '@merlot/common/vueI18nMessageEscaping.js';
import { publishedTranslationFingerprints, publishedPropertyContentByLocale } from './i18n/publishedBundles.js';
import { shouldUseRemoteTranslationCheck } from './utils/i18nRemoteSync.js';
import { shouldSyncPublicCmsFromApi } from './utils/publicCmsSource.js';
import { applySiteBrandHead, mergeSiteBrand } from './utils/siteBrandHead.js';

let unhandledRejectionListenerInstalled = false;

/**
 * Surface Vue and related async errors in the browser console. Production minified builds
 * often give an empty slot / `<!---->` with no default logging when setup() or render throws.
 */
function installClientVueErrorLogging(app) {
  app.config.errorHandler = (err, instance, info) => {
    const vnodeType = instance?.$?.type;
    let component = 'unknown';
    if (typeof vnodeType === 'function') {
      component = vnodeType.name || vnodeType.__name || component;
    } else if (vnodeType && typeof vnodeType === 'object') {
      component = vnodeType.name || vnodeType.__name || component;
    }
    if (instance?.$options?.name) {
      component = instance.$options.name;
    }
    console.error('[Merlot Vue] errorHandler', info, { component }, err);
  };

  if (import.meta.env.DEV) {
    app.config.warnHandler = (msg, _instance, trace) => {
      console.warn('[Merlot Vue] warnHandler', msg, trace);
    };
  }

  if (typeof window !== 'undefined' && !unhandledRejectionListenerInstalled) {
    unhandledRejectionListenerInstalled = true;
    window.addEventListener('unhandledrejection', (event) => {
      console.error('[Merlot Vue] unhandledrejection', event.reason);
    });
  }
}

/**
 * Fetch translations from API for a specific locale
 */
async function fetchTranslations(locale) {
  try {
    const apiUrl = CONFIG.API_BASE_URL;
    const response = await fetch(`${apiUrl}/i18n/${locale}`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data = await response.json();
    return data.success ? (data.translations || {}) : {};
  } catch (error) {
    console.warn(`Failed to fetch translations for ${locale}:`, error.message);
    return {};
  }
}

function seedInitialStatePropertyFromBundle(initialState, locale) {
  const bundle = publishedPropertyContentByLocale[locale];
  const fallbackBundle =
    locale !== CONFIG.DEFAULT_LOCALE
      ? publishedPropertyContentByLocale[CONFIG.DEFAULT_LOCALE]
      : null;
  const property = bundle?.property || fallbackBundle?.property;
  if (!property) return;

  const merged = mergeSiteBrand(
    brandFromProperty(bundle?.property),
    brandFromProperty(fallbackBundle?.property)
  );

  initialState.property = {
    ...property,
    siteName: merged.siteName,
    siteSlogan: merged.siteSlogan
  };
  initialState.floors = Array.isArray(bundle?.floors) ? bundle.floors : [];

  applySiteBrandHead({
    siteName: merged.siteName,
    siteSlogan: merged.siteSlogan
  });
}

function pathLocaleFromWindow() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
  if (m && CONFIG.SUPPORTED_LOCALES.includes(m[1])) {
    return m[1];
  }
  return null;
}

function brandFromProperty(property) {
  if (!property || typeof property !== 'object') return null;
  const siteName = (property.siteName || '').trim();
  const siteSlogan = (property.siteSlogan || '').trim();
  if (!siteName && !siteSlogan) return null;
  return { siteName, siteSlogan };
}

async function hydrate() {
  // Get initial state from server-rendered HTML
  const initialState = window.__INITIAL_STATE__ || {};

  // Check for URL/cookie mismatch (handles browser caching issue)
  // If cookie says one locale but URL says another, redirect to cookie locale
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const cookieLocale = getLocaleFromCookie();
    if (cookieLocale) {
      const currentPath = window.location.pathname;
      // Check if we're on a locale-prefixed route (e.g., /fr/, /en/property)
      const pathMatch = currentPath.match(/^\/([a-z]{2})(\/|$)/);
      if (pathMatch) {
        const urlLocale = pathMatch[1];
        // If cookie locale doesn't match URL locale, redirect to cookie locale
        if (urlLocale !== cookieLocale && CONFIG.SUPPORTED_LOCALES.includes(cookieLocale)) {
          const newPath = currentPath.replace(/^\/[a-z]{2}/, `/${cookieLocale}`);
          console.log(`🔄 Cookie locale (${cookieLocale}) doesn't match URL locale (${urlLocale}), redirecting to ${newPath}`);
          window.location.replace(newPath);
          return; // Stop execution, redirect will load new page
        }
      }
    }
  }

  // Initialize authentication
  await initAuth();

  /**
   * Locale for this load (must not trust initialState.locale alone).
   * Static `spa.html` from the build embeds `locale: "fr"` with empty translations.
   * Using `initialState.locale || getLocaleFromClient()` made F5 on `/login` ignore the
   * `locale` cookie and stick to `fr` until client navigation. Prefer URL prefix, then
   * cookie/browser/default — then align initialState for createApp.
   */
  const pathLocale = pathLocaleFromWindow();
  const locale = pathLocale || CONFIG.getLocaleFromClient();
  initialState.locale = locale;

  // Set cookie if it doesn't exist (for persistence)
  if (typeof document !== 'undefined') {
    const cookieLocale = getLocaleFromCookie();
    if (!cookieLocale) {
      setLocaleCookie(locale);
    }
  }

  // Prod SPA without SHA fingerprint: optional API fallback. Dev uses SSG bundle only.
  const bundledFp = publishedTranslationFingerprints[locale] || '';
  if (
    shouldSyncPublicCmsFromApi() &&
    !shouldUseRemoteTranslationCheck(bundledFp) &&
    (!initialState.translations || Object.keys(initialState.translations).length === 0)
  ) {
    console.log(`📥 Fetching translations from API for SPA route (locale: ${locale})...`);
    const translations = await fetchTranslations(locale);
    if (translations && Object.keys(translations).length > 0) {
      initialState.translations = translations;
      initialState.locale = locale;
    }
  }

  if (!shouldSyncPublicCmsFromApi()) {
    seedInitialStatePropertyFromBundle(initialState, locale);
  }

  // Create Vue app
  const { app, router, i18n } = createApp({
    isSSR: false,
    initialState
  });

  installClientVueErrorLogging(app);

  // Re-apply fetched translations with deep merge (createApp already merged once; this aligns nested keys)
  if (initialState.translations && Object.keys(initialState.translations).length > 0) {
    const currentMessages = i18n.global.messages.value[locale] || {};
    i18n.global.setLocaleMessage(
      locale,
      mergeLocaleMessagesDeep(
        currentMessages,
        escapeVueI18nAtInTranslationsTree(initialState.translations)
      )
    );
  }

  // Wait for router to be ready before mounting (afterEach syncs published i18n once per navigation).
  await router.isReady();

  // Mount the app - Vue will hydrate the existing HTML
  app.mount('#app');

  // Clean up __INITIAL_STATE__ after hydration
  delete window.__INITIAL_STATE__;
}

hydrate();






